import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  FileText,
  FileStack,
  Users,
  ShieldCheck,
  UsersRound,
  Settings,
  ScrollText,
  ClipboardList,
  Mail,
  GitBranch,
  BarChart3,
  Plug,
  ChevronDown,
  FolderLock,
  Inbox as InboxIcon,
  FilePlus2,
} from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../lib/api";
import {
  canAccessCompanyDocuments,
  canManageOrg,
  canManageWorkflows,
  canUploadDocuments,
  canViewAuditLog,
  canViewReports,
} from "../lib/permissions";

// Nav model shared by the desktop sidebar and the mobile drawer (Layout.jsx)
// so the phone never loses routes the desktop has.
export function useNavItems() {
  const { role, currentOrg } = useAuth();
  const { t } = useTranslation();

  // Pending-task badge — polled every 30s per PRD §E.
  // Skipped if no org is selected (no X-Org-Id → request would fail).
  const taskCount = useQuery({
    queryKey: ["tasks", "count", currentOrg?.id],
    queryFn: () => api.countTasks(),
    enabled: Boolean(currentOrg?.id && role),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 25_000,
  });
  const pending = taskCount.data?.pending || 0;

  // Hide items the current role can't act on. Read-only items stay visible
  // (everyone can browse Documents/Contractors/Templates — the write
  // affordances inside each page are gated separately).
  const navItems = [
    {
      to: "/",
      label: t("nav.dashboard"),
      icon: LayoutDashboard,
      testid: "nav-dashboard",
      end: true,
    },
    {
      to: "/inbox",
      label: t("nav.inbox"),
      icon: InboxIcon,
      testid: "nav-inbox",
      badge: pending > 0 ? pending : null,
    },
    {
      to: "/documents",
      label: t("nav.documents"),
      icon: FileText,
      testid: "nav-documents",
    },
    {
      to: "/templates",
      label: t("nav.templates"),
      icon: FileStack,
      testid: "nav-templates",
    },
    {
      to: "/contractors",
      label: t("nav.contractors"),
      icon: Users,
      testid: "nav-contractors",
    },
    {
      to: "/correspondence",
      label: t("nav.correspondence"),
      icon: Mail,
      testid: "nav-correspondence",
    },
    // Phase OC — закрытый раздел: только owner + юрист.
    canAccessCompanyDocuments(role)
      ? {
          to: "/company-documents",
          label: t("nav.company_documents"),
          icon: FolderLock,
          testid: "nav-company-documents",
        }
      : null,
    // Phase R: мощные инструменты собраны в «Автоматизацию» — ядро
    // (документы/шаблоны) впереди, конструкторы не шумят для подписантов.
    canManageWorkflows(role)
      ? {
          group: "Автоматизация",
          icon: GitBranch,
          testid: "nav-automation",
          children: [
            {
              to: "/workflows",
              label: t("nav.workflows"),
              icon: GitBranch,
              testid: "nav-workflows",
            },
            {
              to: "/forms",
              label: t("nav.forms"),
              icon: ClipboardList,
              testid: "nav-forms",
            },
          ],
        }
      : {
          to: "/forms",
          label: t("nav.forms"),
          icon: ClipboardList,
          testid: "nav-forms",
        },
    {
      to: "/team",
      label: t("nav.team"),
      icon: UsersRound,
      testid: "nav-team",
    },
    canViewReports(role)
      ? {
          to: "/reports",
          label: t("nav.reports"),
          icon: BarChart3,
          testid: "nav-reports",
        }
      : null,
    // Интеграции — открытое API / 1С / webhooks. Отдельный пункт (как в
    // Documentolog), доступен владельцу и администраторам.
    canManageOrg(role)
      ? {
          to: "/integrations",
          label: "Интеграции",
          icon: Plug,
          testid: "nav-integrations",
        }
      : null,
    // Phase R: настройки — ОДИН пункт; организация/уведомления/журнал —
    // табы внутри страницы.
    {
      to: "/settings",
      label: t("nav.settings"),
      icon: Settings,
      testid: "nav-settings",
    },
  ].filter(Boolean);

  return { navItems, showUploadCta: canUploadDocuments(role), t };
}

function NavItemLink({ item, onNavigate, testidPrefix, nested = false }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`${testidPrefix}${item.testid}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors duration-150 ${
          nested ? "ml-6" : ""
        } ${
          isActive
            ? "bg-primary text-white font-medium"
            : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
      <span className="flex-1">{item.label}</span>
      {item.badge != null && (
        <span
          className="ml-auto bg-primary/15 text-primary text-[11px] font-medium rounded-full px-2 py-0.5 leading-tight"
          data-testid={`${testidPrefix}${item.testid}-badge`}
        >
          {item.badge > 99 ? "99+" : item.badge}
        </span>
      )}
    </NavLink>
  );
}

// Phase R: collapsible-группа («Автоматизация»). Открыта, если активный
// роут внутри неё; состояние локальное — пользователь может свернуть шум.
function NavGroup({ item, onNavigate, testidPrefix }) {
  const location = useLocation();
  const containsActive = (item.children || []).some((c) =>
    location.pathname.startsWith(c.to),
  );
  const [open, setOpen] = React.useState(containsActive);
  React.useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);
  const Icon = item.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}${item.testid}`}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
        aria-expanded={open}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
        <span className="flex-1 text-left">{item.group}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && (
        <div className="space-y-0.5 mt-0.5">
          {(item.children || []).map((child) => (
            <NavItemLink
              key={child.to}
              item={child}
              onNavigate={onNavigate}
              testidPrefix={testidPrefix}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Nav list renderer shared by sidebar + mobile drawer. `onNavigate` lets the
// drawer close itself when a route is chosen.
export function SidebarNav({ navItems, onNavigate, testidPrefix = "" }) {
  return (
    <nav className="flex-1 px-3 py-3 space-y-0.5">
      {navItems.map((item) =>
        item.group ? (
          <NavGroup
            key={item.group}
            item={item}
            onNavigate={onNavigate}
            testidPrefix={testidPrefix}
          />
        ) : (
          <NavItemLink
            key={item.to}
            item={item}
            onNavigate={onNavigate}
            testidPrefix={testidPrefix}
          />
        ),
      )}
    </nav>
  );
}

export default function Sidebar() {
  const { navItems, showUploadCta, t } = useNavItems();

  return (
    <aside
      data-testid="app-sidebar"
      className="hidden md:flex md:flex-col w-64 shrink-0 border-r border-zinc-900/10 bg-parchment h-full"
    >
      <div className="px-6 pt-7 pb-6 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-lg font-semibold leading-none tracking-tight text-zinc-900">
              Signa
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-900/40 mt-1">
              ЭЦП · NCA Layer
            </div>
          </div>
        </div>
      </div>

      {/* Список прокручивается сам — подвал с кнопкой «Создать документ»
          остаётся на экране при любой длине навигации и любой длине страницы. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <SidebarNav navItems={navItems} />
      </div>

      {/* UX-26: кнопка «Загрузить» здесь дублировала точно такую же в шапке —
          на десктопе обе видны одновременно, плюс третья на самой странице.
          Оставляем одну глобальную (в шапке, она есть и на мобильном), а тут —
          только пояснение про NCA Layer. */}
      {showUploadCta && (
        <div className="p-3 border-t border-zinc-900/10 space-y-2 shrink-0">
          {/* «Загрузить» переехала в шапку (UX-аудит), здесь остаётся второй
              вход — создание внутреннего документа по виду: служебная записка
              или приказ не существуют файлом, они рождаются из бланка. */}
          <NavLink
            to="/documents/new"
            data-testid="nav-create-cta"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors btn-pill"
          >
            <FilePlus2 className="h-4 w-4" />
            Создать документ
          </NavLink>
          <div className="mt-3 text-[11px] text-zinc-900/40 leading-relaxed px-1">
            Подписание через NCA Layer. Требуется ЭЦП (НУЦ РК).
          </div>
        </div>
      )}
    </aside>
  );
}
